// @ts-ignore
import { buttonColors, dialogMaxWidths } from '@ghs/components';
import { GridRowId, GridRowModes } from '@mui/x-data-grid-pro';
import { attach, createEffect, createEvent, createStore, sample, split } from 'effector';
import { persist as persistToLocalStorage } from 'effector-storage/local';
import { persist } from 'effector-storage/query';
import { isEmpty, keyBy, pick, uniq } from 'lodash';
import { debounce, delay, spread } from 'patronum';
import { createElement } from 'react';
import { $$reseller } from '../../services/ResellerService/model.js';
import { getAffectedField, recalculateVersionDates, validateVersionDates } from '../../services/ResellerService/utils.js';
import { treeShakeObject, VOID } from '../../util/JsonUtils.js';
import createDataGridModel from '../../util/createDataGridModel';
import { createDialog } from '../../util/createDialog.js';
import { $$batchEditVersionDatesDialogModel } from '../BatchEditVersionDatesDialog/model.js';
import { $$batchEditVersionNameDialog } from '../BatchEditVersionNameDialog/model.js';
import { $$confirmationDialog } from '../ConfirmationDialog/model.js';
import { $$snackbar } from '../SnackbarRoot/model.js';
import VersionsDeleteConfirmationDialog from './VersionsDeleteConfirmationDialog.jsx';

const dialog = createDialog<{ campaignId: Reseller.Campaign['id'] }>();

const $search = createStore<string>('');

const $isRecalculationEnabled = createStore<boolean>(true);

const $campaign = sample({
  source: {
    campaigns: $$reseller.$campaigns,
    campaignId: dialog.$state.map(state => Number(state?.campaignId) || null)
  },
  fn: ({ campaigns, campaignId }) => campaigns.find(({ id }) => id === campaignId) || null
});

const campaignIdChanged = createEvent<Reseller.Campaign['id'] | null>();

const searchChanged = createEvent<string>();

const realculateToggleChanged = createEvent<boolean>();

const deleteSingleClicked = createEvent<GridRowId>();

const deleteSelectedClicked = createEvent<void>();

const deleteByIdsTriggered = createEvent<Reseller.Version['id'][]>();

const openUnsavedCloseConfirmation = createEvent<{ saveAll: () => void; discardChanges: () => void; restoreFocusTo?: { id: GridRowId; field: string } }>();

const triggerVersionDateValidation = createEvent<GridRowId[]>();

const saveAllClicked = createEvent<void>();

const discardClicked = createEvent<void>();

const singleRowSaveClicked = createEvent<GridRowId>();

/**
 * Set right before `singleRowSaveClicked` when the save was triggered by pressing Enter on the
 * last editable cell of a row (Excel-style navigation): once the save succeeds, that row id is
 * put into edit mode and focused, continuing the flow into the next row.
 */
const nextRowIdAfterSaveSet = createEvent<GridRowId>();

const $nextRowIdAfterSave = createStore<GridRowId | null>(null).on(nextRowIdAfterSaveSet, (_, id) => id);

/**
 * Set right before `singleRowSaveClicked` when the save was triggered by pressing Enter on a row's
 * last editable cell or by clicking outside an edited row (as opposed to clicking the row's own Save
 * icon): remembers which cell had focus, so that if the user cancels the save prompt instead, focus
 * can be put back there rather than left dangling outside the grid - which would itself read as
 * "left the row" and reopen the very prompt just dismissed.
 */
const rowFieldPendingRestoreSet = createEvent<{ id: GridRowId; field: string }>();

const $rowFieldPendingRestore = createStore<{ id: GridRowId; field: string } | null>(null).on(rowFieldPendingRestoreSet, (_, cell) => cell);

/**
 * A cell that needs its focus (re)applied once its row is confirmed to be in edit mode: either the
 * next row's first editable cell after an Excel-style Enter-triggered save (`field: null`), or the
 * original cell restored after the user cancels a save prompt (`field` set to that specific column).
 * Consumed (and cleared) by the table component through a plain `useEffect`, since moving DataGrid's
 * cell focus needs to happen inside React's normal render/commit cycle - calling the grid api's
 * `setCellFocus` from this model's own async/callback code does not reliably result in real DOM focus.
 */
const cellPendingFocusSet = createEvent<{ id: GridRowId; field: string | null }>();

const cellPendingFocusCleared = createEvent();

const $cellPendingFocus = createStore<{ id: GridRowId; field: string | null } | null>(null)
  .on(cellPendingFocusSet, (_, cell) => cell)
  .reset(cellPendingFocusCleared);

const editClicked = createEvent<GridRowId>();

const cancelClicked = createEvent<GridRowId>();

const rowsModelChanged = createEvent<import('@mui/x-data-grid').GridRowModesModel>();

const secondConfirmationTriggered = createEvent<GridRowId[]>();

const confirmSaveDialogOpened = createEvent<Reseller.Version[]>();

const cleanupAndValidateUnsavedChanges = createEvent();

const validateNameNumberFx = createEffect<Reseller.Version[], DataGridError<Reseller.Version>[]>(async updatedRows => {
  const backendValidationResult = await Promise.all(updatedRows.map(version => pick(version, ['id', 'name', 'number', 'campaignId'])).map($$reseller.validateVersionNameFx));
  const errorMessage = 'The same Version Name / Version Number combination already exists in the campaign and can’t be duplicated.';
  return backendValidationResult
    .map((isValid, index) => (isValid ? null : index))
    .filter(index => index !== null)
    .map(index => [
      { id: updatedRows[index].id, field: 'name' as const, error: errorMessage },
      { id: updatedRows[index].id, field: 'number' as const, error: errorMessage }
    ])
    .flat();
});

const getFreshRowsFx = attach({
  source: {
    campaignId: dialog.$state.map(state => state?.campaignId || null),
    search: $search
  },
  mapParams: (
    pagination: DataGrid.ServerSideParams,
    filters: {
      campaignId: Reseller.Version['campaignId'] | null;
      search: string;
    }
  ) => treeShakeObject({ ...pagination, ...filters }),
  effect: $$reseller.getVersionsFx
});

const $$versions = createDataGridModel({
  type: 'server',
  effect: getFreshRowsFx,
  refetch: [debounce($search.updates, 100), $$reseller.deleteVersionsFx.done]
}) as unknown as DataGridFactory<Reseller.Version>; // After createDataGridModel is typed, this cast can be removed

const getMultipleInHomeWindowsDetailFx = createEffect<
  {
    mailClassIds: Reseller.Version['mailClass']['id'][];
    customerId: Reseller.Campaign['customer']['id'];
    lineOfBusinessId: Reseller.LineOfBusiness['id'];
  },
  unknown
>(async ({ customerId, lineOfBusinessId, mailClassIds }) => {
  return Promise.all(
    mailClassIds.map(mailClassId =>
      $$reseller.getInHomeWindowsDetailFx({
        customerId,
        lineOfBusinessId,
        mailClassId
      })
    )
  );
});

split({
  // @ts-ignore
  source: campaignIdChanged,
  // @ts-ignore
  match: value => (value === null ? 'close' : 'open'),
  cases: {
    // @ts-ignore
    open: dialog.open.prepend(campaignId => ({ campaignId })),
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    close: dialog.close.prepend(() => {})
  }
});

// @ts-ignore
persist({
  store: dialog.$state.map(state => state?.campaignId || null),
  key: 'campaign',
  target: campaignIdChanged
});

sample({
  source: searchChanged,
  target: $search
});

sample({
  source: realculateToggleChanged,
  target: $isRecalculationEnabled
});

persistToLocalStorage({
  store: $isRecalculationEnabled,
  key: 'edit-campaigns:edit-versions:recalculate-in-home-dates-enabled'
});

sample({
  // @ts-ignore
  clock: delay(secondConfirmationTriggered, 0), // Delay is needed to let the confirmation dialog close before opening it again
  fn: ids => ({
    confirmation: {
      title: 'Success!',
      content: 'All your changes have been successfully applied.',
      acceptButtonText: 'Save and exit',
      closeButtonText: 'Undo Changes',
      onAccept: () => {
        // @ts-ignore
        $$reseller.deleteVersionsFx(ids);
      }
    },
    highlight: ids
  }),
  target: spread({
    confirmation: $$confirmationDialog.open,
    highlight: $$versions.highlightRows
  })
});

sample({
  clock: deleteSingleClicked.map(id => [id]),
  fn: ids => ids.map(Number),
  target: deleteByIdsTriggered
});

sample({
  source: $$versions.$selectedRowsIds,
  clock: deleteSelectedClicked,
  fn: ids => ids.map(Number),
  target: deleteByIdsTriggered
});

sample({
  source: $$versions.$data,
  clock: deleteByIdsTriggered,
  fn: (versions, idsToDelete) => ({
    title: 'Version deletion confirmation',
    content: createElement(VersionsDeleteConfirmationDialog, { versions: versions.filter(v => idsToDelete.includes(v.id)) }),
    onAccept: () => secondConfirmationTriggered(idsToDelete),
    acceptButtonText: 'Delete all selected',
    acceptButtonColor: buttonColors.ERROR,
    maxWidth: dialogMaxWidths.XL
  }),
  target: $$confirmationDialog.open
});

sample({
  // @ts-ignore
  clock: openUnsavedCloseConfirmation,
  fn: ({ saveAll, discardChanges, restoreFocusTo }) => ({
    title: 'Wait!',
    content: 'You have unsaved changes. Are you sure you want to leave? If you exit now, all changes will be lost.',
    maxWidth: dialogMaxWidths.SM,
    // See the equivalent comment on the save-confirmation dialog above: without this, MUI's Modal
    // restoring focus once its closing transition ends could read as the row losing focus again and
    // reopen this same prompt (relevant when this was triggered by clicking outside an edited row,
    // rather than by closing the whole Edit Versions dialog).
    disableRestoreFocus: true,
    onClose: () => {
      if (restoreFocusTo) {
        cellPendingFocusSet(restoreFocusTo);
      }
    },
    buttons: [
      {
        children: 'Save all changes',
        // `saveAll` (`saveAllClicked`) validates and then re-opens this same confirmation dialog with
        // its own "Success!" content, so this must not close it - closing here would immediately hide
        // that follow-up dialog before the user (or `onAccept`) ever gets to act on it.
        onClick: () => saveAll(),
        variant: 'outlined'
      },
      {
        children: 'Exit anyway',
        onClick: () => {
          discardChanges();
          $$confirmationDialog.close();
        },
        variant: 'outlined'
      },
      {
        children: 'Cancel',
        onClick: () => {
          if (restoreFocusTo) {
            cellPendingFocusSet(restoreFocusTo);
          }
          $$confirmationDialog.close();
        },
        variant: 'contained'
      }
    ]
  }),
  target: $$confirmationDialog.open
});

sample({
  source: { updatedRows: $$versions.$updatedRows, existingErrors: $$versions.$errors },
  clock: [saveAllClicked.map(() => 'all'), singleRowSaveClicked],
  fn: ({ updatedRows, existingErrors }, id) => {
    const selectedUpdatedRows = updatedRows.filter(v => id === 'all' || v.id === id);
    const dateValidationErrors = selectedUpdatedRows.map(validateVersionDates).flat();

    return {
      updatedRows: selectedUpdatedRows,
      updatedErrors: [...existingErrors, ...dateValidationErrors]
    };
  },
  target: spread({ updatedRows: confirmSaveDialogOpened, updatedErrors: $$versions.setErrors })
});

sample({
  source: { updatedRows: $$versions.$updatedRows, existingErrors: $$versions.$errors },
  clock: triggerVersionDateValidation,
  fn: ({ updatedRows, existingErrors }, ids) => {
    const selectedUpdatedRows = updatedRows.filter(v => ids.includes(v.id));
    const dateValidationErrors = selectedUpdatedRows.map(validateVersionDates).flat();

    return {
      updatedErrors: [...existingErrors, ...dateValidationErrors]
    };
  },
  target: spread({ updatedErrors: $$versions.setErrors })
});

sample({
  source: { errors: $$versions.$errors, nextRowIdAfterSave: $nextRowIdAfterSave, rowFieldPendingRestore: $rowFieldPendingRestore },
  clock: confirmSaveDialogOpened,
  filter: ({ errors }) => isEmpty(errors),
  fn: ({ nextRowIdAfterSave, rowFieldPendingRestore }, updatedRows) => {
    // ConfirmationDialog calls `onClose` after a successful `onAccept` too (not just on Cancel/X/
    // backdrop), so this flag is needed to tell the two apart and avoid onClose's cell-restore
    // clobbering onAccept's "advance to the next row" focus target.
    let wasAccepted = false;
    return {
      title: 'Success!',
      content: 'All your changes have been successfully applied.',
      // MUI's Modal restoring focus to the cell that was focused before this dialog opened, once its
      // closing transition finishes, reads to the DataGrid as the user leaving the row all over again -
      // re-triggering this very save flow and reopening the dialog. Disabling that restoration avoids
      // the loop; leaving focus on whichever dialog button was clicked is an acceptable trade-off here.
      disableRestoreFocus: true,
      onAccept: async () => {
        wasAccepted = true;
        await $$reseller.updateVersionsFx(updatedRows);
        if (nextRowIdAfterSave === null) {
          return;
        }
        $$versions.setRowMode({ id: nextRowIdAfterSave, mode: GridRowModes.Edit });
        cellPendingFocusSet({ id: nextRowIdAfterSave, field: null });
      },
      // Canceling (or dismissing via the X icon/backdrop) declines the save but must not leave focus
      // outside the grid - that alone would read as the row losing focus again and reopen this same
      // prompt. Put focus back on the exact cell that triggered it instead.
      onClose: () => {
        if (!wasAccepted && rowFieldPendingRestore) {
          cellPendingFocusSet(rowFieldPendingRestore);
        }
      },
      acceptButtonText: 'Save and exit',
      closeButtonText: 'Undo changes'
    };
  },
  target: $$confirmationDialog.open
});

// These pending markers only need to survive long enough to be captured in the onAccept/onClose
// closures above; clear them once consumed so a plain Save (icon/save-all) never picks up a stale
// value left over from an earlier Enter-triggered or click-outside-triggered save.
sample({
  clock: confirmSaveDialogOpened,
  fn: () => null,
  target: $nextRowIdAfterSave
});

sample({
  clock: confirmSaveDialogOpened,
  fn: () => null,
  target: $rowFieldPendingRestore
});

sample({
  source: $$versions.$errors,
  clock: validateNameNumberFx.done,
  filter: (_existingErrors, { result }) => result.length > 0,
  fn: (existingErrors, { result: errors }) => [...existingErrors, ...errors],
  target: $$versions.setErrors
});

sample({
  clock: $$reseller.deleteVersionsFx.done,
  fn: ({ params }) => ({
    unselectAllRows: VOID,
    snackbar: {
      message: params.length > 1 ? 'Campaign versions deleted' : 'Campaign version deleted',
      severity: 'success' as const
    }
  }),
  target: spread({ unselectAllRows: $$versions.unselectAllRows, snackbar: $$snackbar.open })
});

sample({
  clock: $$reseller.deleteVersionsFx.fail,
  fn: () => ({
    unselectAllRows: VOID,
    snackbar: {
      message: 'Error deleting version(s)',
      severity: 'error' as const
    }
  }),
  target: spread({ unselectAllRows: $$versions.unselectAllRows, snackbar: $$snackbar.open })
});

sample({
  source: $$versions.$data,
  clock: $$reseller.updateVersionsFx.done,
  fn: (data, { params: updatedRows }) => ({
    unselectAllRows: VOID,
    resetAllChanges: VOID,
    setAllRowsMode: GridRowModes.View,
    data: data.map(version => updatedRows.find(({ id }) => id === version.id) || version)
  }),
  target: spread({
    unselectAllRows: $$versions.unselectAllRows,
    resetAllChanges: $$versions.resetAllChanges,
    setAllRowsMode: $$versions.setAllRowsMode,
    data: $$versions.$data
  })
});

sample({
  clock: $$reseller.updateVersionsFx.fail,
  fn: () => ({
    unselectAllRows: VOID,
    resetAllChanges: VOID,
    setAllRowsMode: GridRowModes.View,
    snackbar: {
      message: 'Error updating version(s)',
      severity: 'error' as const
    }
  }),
  target: spread({
    unselectAllRows: $$versions.unselectAllRows,
    resetAllChanges: $$versions.resetAllChanges,
    setAllRowsMode: $$versions.setAllRowsMode,
    snackbar: $$snackbar.open
  })
});

sample({
  source: {
    campaignId: dialog.$state.map(state => state?.campaignId || null),
    unsavedChanges: $$versions.$unsavedChanges
  },
  clock: [
    $$batchEditVersionNameDialog.changesApproved.map(({ campaignId, versionNames: data }) => ({ campaignId, data })),
    $$batchEditVersionDatesDialogModel.changesApproved.map(({ campaignId, versionsDates: data }) => ({
      campaignId,
      data
    }))
  ],
  filter: ({ campaignId }, { campaignId: receivedCampaignId }) => campaignId === receivedCampaignId,
  fn: ({ unsavedChanges }, { data }) => {
    const newChanges = data
      .map(({ id, ...updatedCell }) =>
        Object.keys(updatedCell).map(field => ({
          id,
          field,
          value: updatedCell[field as 'name']
        }))
      )
      .flat() as DataGridChangeUnit<Reseller.Version>[];
    const newUnsavedChanges = [...unsavedChanges.filter(({ id, field }) => !newChanges.some(c => c.id === id && c.field === field)), ...newChanges];
    const rowIdsUpdated = data.map(v => v.id);
    return {
      unsavedChanges: newUnsavedChanges,
      resetRowsErrorsForFields: {
        ids: newUnsavedChanges.map(({ id }) => id),
        fields: ['firstMailDate', 'lastMailDate', 'inHomeStartDate', 'inHomeEndDate']
      },
      triggerCleanupAndVersionDateValidation: rowIdsUpdated
    };
  },
  target: spread({
    unsavedChanges: $$versions.$unsavedChanges,
    resetRowsErrorsForFields: $$versions.resetRowsErrorsForFields,
    triggerCleanupAndVersionDateValidation: cleanupAndValidateUnsavedChanges
  })
});

sample({
  clock: discardClicked,
  target: [$$versions.unselectAllRows, $$versions.resetAllChanges, $$versions.setAllRowsMode.prepend(() => GridRowModes.View)]
});

sample({
  // @ts-ignore
  source: {
    versions: $$versions.$data,
    isRecalculationEnabled: $isRecalculationEnabled,
    unsavedChanges: $$versions.$unsavedChanges,
    inHomeWindowRanges: $$reseller.$inHomeWindowRanges,
    campaign: $campaign
  },
  clock: $$versions.cellValueChanged,
  fn: ({ unsavedChanges, versions, isRecalculationEnabled, inHomeWindowRanges, campaign }, { id, field, value }) => {
    const version = keyBy(versions, 'id')[id] as Reseller.Version | undefined;
    const inHomeWindowRange = inHomeWindowRanges.find(
      range => range.mailClassId === version?.mailClass.id && range.lineOfBusinessId === campaign?.lineOfBusiness.id && range.customerId === campaign?.customer.id
    ) as Reseller.InHomeWindowRange | undefined;
    if (!version || !inHomeWindowRange) {
      return {};
    }
    // @ts-ignore
    const { changes, isRecalculationEnabled: newIsRecalculationEnabled } = recalculateVersionDates({
      field,
      value,
      version,
      isRecalculationEnabled,
      inHomeWindowRange
    });

    const versionDateRecalculationAffectedField = getAffectedField(field, isRecalculationEnabled);

    return {
      unsavedChanges: [
        ...unsavedChanges.filter(item => !(versionDateRecalculationAffectedField && item.field === versionDateRecalculationAffectedField)),
        ...Object.entries(changes).map(([f, v]) => ({
          id,
          field: f,
          value: v
        }))
      ],
      isRecalculationEnabled: newIsRecalculationEnabled,
      resetRowsErrorsForFields: {
        ids: [id],
        fields: ['firstMailDate', 'lastMailDate', 'inHomeStartDate', 'inHomeEndDate']
      },
      triggerCleanupAndVersionDateValidation: [id]
    };
  },
  target: spread({
    isRecalculationEnabled: $isRecalculationEnabled,
    unsavedChanges: $$versions.$unsavedChanges,
    resetRowsErrorsForFields: $$versions.resetRowsErrorsForFields,
    triggerCleanupAndVersionDateValidation: cleanupAndValidateUnsavedChanges
  })
});

sample({
  source: {
    data: $$versions.$data,
    unsavedChanges: $$versions.$unsavedChanges
  },
  clock: cleanupAndValidateUnsavedChanges,
  fn: ({ data, unsavedChanges }) => unsavedChanges.filter(c => data.find(row => row.id === c.id)?.[c.field] !== c.value),
  target: $$versions.$unsavedChanges
});

sample({
  clock: cleanupAndValidateUnsavedChanges,
  fn: ids => ids,
  target: triggerVersionDateValidation
});

sample({
  clock: $$reseller.updateVersionsFx.done.map(({ params }) => params.map(version => version.id)),
  target: $$versions.highlightRows
});

sample({
  clock: editClicked.map(id => ({ id, mode: GridRowModes.Edit })),
  target: $$versions.setRowMode
});

sample({
  clock: cancelClicked,
  fn: id => ({ setRowMode: { id, mode: GridRowModes.View }, resetRowChanges: id }),
  target: spread({ setRowMode: $$versions.setRowMode, resetRowChanges: $$versions.resetRowChanges })
});

sample({
  clock: dialog.close,
  target: $search.reinit
});

sample({
  clock: $$versions.cellValueChanged,
  filter: ({ field }) => field === 'name' || field === 'number',
  fn: ({ id }) => ({ id, field: 'name' as const }),
  target: $$versions.resetCellError
});

sample({
  source: $$versions.$updatedRows,
  clock: debounce($$versions.cellValueChanged, 1000),
  filter: (_unused, { field }) => field === 'name' || field === 'number',
  fn: (updatedRows, { id }) => updatedRows.filter(v => v.id === id),
  target: validateNameNumberFx
});

sample({
  source: {
    customerId: $campaign.map(campaign => campaign?.customer.id || -1),
    lineOfBusinessId: $campaign.map(campaign => campaign?.lineOfBusiness.id || -1),
    mailClassIds: $$versions.$data.map(data => uniq(data.map(version => version.mailClass.id)))
  },
  clock: [$$versions.tableMounted.filter({ fn: Boolean }), $$versions.$pagination.updates],
  filter: ({ lineOfBusinessId, customerId }) => lineOfBusinessId !== -1 && customerId !== -1,
  target: getMultipleInHomeWindowsDetailFx
});

const $selectedVersionWithScans = sample({
  source: {
    selectedRowIds: $$versions.$selectedRowsIds,
    versions: $$versions.$data
  },
  fn: ({ selectedRowIds, versions }) => versions.some(v => selectedRowIds.includes(v.id) && v.hasScans === true)
});

export const $$campaignVersionsDialog = {
  ...dialog,

  $$versions,

  $campaign,
  $isRecalculationEnabled: $isRecalculationEnabled.map(d => d),
  $search: $search.map(d => d),
  $selectedVersionWithScans: $selectedVersionWithScans.map(d => d),
  $isValidationPending: validateNameNumberFx.pending,
  $isSavePending: $$reseller.updateVersionsFx.pending,
  $cellPendingFocus: $cellPendingFocus.map(d => d),

  searchChanged,
  realculateToggleChanged,
  deleteSingleClicked,
  deleteSelectedClicked,
  openUnsavedCloseConfirmation,
  saveAllClicked,
  discardClicked,
  singleRowSaveClicked,
  nextRowIdAfterSaveSet,
  rowFieldPendingRestoreSet,
  cellPendingFocusCleared,
  editClicked,
  cancelClicked,
  rowsModelChanged
};
